import React from "react";
import HomeNav from "../navigations/homenav";
import Footer from "../navigations/Footer";
import { useLanguageContext } from "../navigations/homenav";
import {
  BookOpenIcon,
  GlobeAltIcon,
  ClipboardDocumentListIcon,
  DocumentTextIcon,
  ArrowTopRightOnSquareIcon,
  ArrowDownTrayIcon,
  InboxArrowDownIcon,
} from "@heroicons/react/24/outline";

const SectionHeader = ({ title, subtitle }) => (
  <div className="max-w-3xl mx-auto text-center mb-12">
    <h2
      className="text-3xl md:text-4xl font-bold"
      style={{ color: "var(--text-primary)" }}
    >
      {title}
    </h2>
    {subtitle && (
      <p
        className="mt-4 text-lg md:text-xl"
        style={{ color: "var(--text-secondary)" }}
      >
        {subtitle}
      </p>
    )}
  </div>
);

const TagPill = ({ children }) => (
  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-sky-50 text-sky-700 border border-sky-100">
    {children}
  </span>
);

const ResourceCard = ({ resource }) => {
  const { t } = useLanguageContext();
  return (
    <div className="card-bg rounded-2xl shadow-md hover:shadow-xl transition-shadow duration-300 flex flex-col h-full">
      <div className="p-6 flex-1 flex flex-col">
        <div className="flex items-center justify-between mb-3">
          <TagPill>{resource.tag}</TagPill>
          {resource.year && (
            <span className="text-xs font-medium text-slate-500">
              {resource.year}
            </span>
          )}
        </div>
        <h3
          className="text-lg md:text-xl font-semibold mb-2"
          style={{ color: "var(--text-primary)" }}
        >
          {resource.title}
        </h3>
        {resource.level && (
          <p className="text-xs uppercase tracking-wide text-slate-500 mb-2">
            {resource.level}
          </p>
        )}
        <p
          className="text-sm md:text-base flex-1"
          style={{ color: "var(--text-secondary)" }}
        >
          {resource.description}
        </p>

        <div className="mt-4 flex flex-wrap gap-3">
          {resource.link && (
            <a
              href={resource.link}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center text-sm font-medium text-sky-600 hover:text-sky-700 hover:underline"
            >
              <ArrowTopRightOnSquareIcon className="h-4 w-4 mr-1" />
              {t('policiesViewDocument')}
            </a>
          )}
          {resource.download && (
            <a
              href={resource.download}
              download
              className="inline-flex items-center text-sm font-medium text-teal-700 hover:text-teal-800"
            >
              <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
              {t('policiesDownloadPdf')}
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

const DisabilityPoliciesPage = () => {
  const { t } = useLanguageContext();


  // National policies & frameworks (Ethiopia-focused)
  const nationalPolicies = [
    {
      title: "National Plan of Action of Persons with Disabilities (2012–2021)",
      year: "2012–2021",
      level: "National policy framework – Ethiopia",
      tag: "National Policy",
      description:
        "Multi-sector national plan that guides implementation of disability rights in Ethiopia – covering access to education, health, social protection, employment and participation.",
      link: "/docs/national/national_plan_of_action_of_persons_with_disabilities_2012_2021_.pdf",
      download:
        "/docs/national/national_plan_of_action_of_persons_with_disabilities_2012_2021_.pdf",
    },
    {
      title: "National Social Protection Policy of Ethiopia",
      year: "2014",
      level: "National policy – Ethiopia",
      tag: "Social Protection",
      description:
        "Defines social protection instruments such as safety nets, social insurance and care services, with specific focus on persons with disabilities and other vulnerable groups.",
      link: "/docs/national/national-social-protection-policy-2014.pdf",
      download: "/docs/national/national-social-protection-policy-2014.pdf",
    },
    {
      title: "Building Proclamation No. 624/2009 – Accessibility Standards",
      year: "2009",
      level: "Legislation – Ethiopia",
      tag: "Accessibility Law",
      description:
        "Sets requirements for accessible buildings and public spaces, including ramps, lifts, signage and other features that enable equal access for persons with disabilities.",
      link: "/docs/national/building-proclamation-624-2009-accessibility.pdf",
      download:
        "/docs/national/building-proclamation-624-2009-accessibility.pdf",
    },
    {
      title:
        "Inclusive Education Strategy for Children with Special Needs / Disabilities",
      year: "Ministry of Education",
      level: "Education strategy – Ethiopia",
      tag: "Inclusive Education",
      description:
        "Outlines the government’s commitment to inclusive and special needs education – teacher training, accessible learning materials, school-level support structures and resource centers.",
      link: "/docs/national/inclusive-education-strategy-children-with-special-needs.pdf",
      download:
        "/docs/national/inclusive-education-strategy-children-with-special-needs.pdf",
    },
    {
  title: "UN Convention on the Rights of Persons with Disabilities (CRPD) – Amharic & English",
  year: "2006",
  level: "International Convention",
  tag: "UN Treaty",
  description:
    "Bilingual Amharic–English text of the UN Convention on the Rights of Persons with Disabilities and its Optional Protocol, setting out States Parties’ obligations to promote, protect and ensure the full and equal enjoyment of all human rights by persons with disabilities.",
  link: "/docs/international/un-crpd-amharic-english.pdf",
  download: "/docs/international/un-crpd-amharic-english.pdf",
}

  ];


  // International conventions & regional instruments
  const internationalFrameworks = [
    {
      title: "UN Convention on the Rights of Persons with Disabilities (CRPD)",
      year: "UN, 2006 (Ethiopia ratified 2010)",
      level: "Global human rights treaty",
      tag: "UN Convention",
      description:
        "Core international treaty that recognizes disability as a human rights and development issue. It sets legally binding standards on equality, accessibility, inclusion and participation.",
      link: "https://www.un.org/development/desa/disabilities/convention-on-the-rights-of-persons-with-disabilities.html",
      external: true,
    },
    {
      title:
        "Optional Protocol to the Convention on the Rights of Persons with Disabilities",
      year: "UN, 2006",
      level: "Global human rights mechanism",
      tag: "Optional Protocol",
      description:
        "Allows individuals and groups to bring complaints to the UN Committee on the Rights of Persons with Disabilities when national remedies have been exhausted.",
      link: "https://www.ohchr.org/en/instruments-mechanisms/instruments/optional-protocol-convention-rights-persons-disabilities",
      external: true,
    },
    {
      title:
        "Protocol to the African Charter on Human and Peoples’ Rights on the Rights of Persons with Disabilities in Africa",
      year: "African Union, 2018",
      level: "Regional treaty – African Union",
      tag: "African Disability Protocol",
      description:
        "Regional human rights instrument that complements the CRPD and responds to the African context – including poverty, harmful practices, conflict and access to justice.",
      link: "https://au.int/en/treaties/protocol-african-charter-human-and-peoples-rights-rights-persons-disabilities-africa",
      external: true,
    },
    {
      title: "Sustainable Development Goals – Disability Inclusion",
      year: "UN 2030 Agenda",
      level: "Global development framework",
      tag: "SDGs",
      description:
        "Disability is referenced explicitly in SDGs 4, 8, 10, 11 and 17, and implicitly across the entire 2030 Agenda, calling for inclusive and equitable development for all.",
      link: "https://sdgs.un.org/topics/disability",
      external: true,
    },
  ];


  // Research & academic work
  const researchPapers = [
    {
      title: "World Report on Disability",
      year: "WHO & World Bank, 2011",
      level: "Global research report",
      tag: "Global Evidence",
      description:
        "Landmark global report providing data on prevalence of disability, barriers to participation, and evidence-based recommendations for health, rehabilitation, support services and inclusion.",
      link: "http://www.who.int/disabilities/world_report/2011/report.pdf",
      external: true,
    },
    {
      title:
        "Rights of Persons with Disabilities – Ethiopia Country Analysis (Sida)",
      year: "Sida, 2014",
      level: "Country study – Ethiopia",
      tag: "Country Study",
      description:
        "Explores the situation of persons with disabilities in Ethiopia, including legal reforms, institutional arrangements, service gaps and recommendations for development partners.",
      link: "/docs/research/rights-of-persons-with-disabilities-ethiopia-sida.pdf",
      download:
        "/docs/research/rights-of-persons-with-disabilities-ethiopia-sida.pdf",
    },
    {
      title:
        "Inclusive Education Flagship Report – What an Inclusive, Equitable, Quality Education Means to Us",
      year: "IDA, 2020",
      level: "Global/Regional – includes African cases",
      tag: "Inclusive Education",
      description:
        "Voices of learners with disabilities and their organizations on what inclusive education should look like in practice – key principles, barriers and good practices.",
      link: "https://www.internationaldisabilityalliance.org/publications/ida-flagship-report-inclusive-education",
      external: true,
    },
    {
      title:
        "Disability-Inclusive Development and Social Protection – Lessons from Ethiopia",
      year: "Policy / working paper",
      level: "Ethiopia-focused analysis",
      tag: "Policy Brief",
      description:
        "Synthesis of emerging research on how social protection programmes, livelihood schemes and community support services can be made disability-inclusive in the Ethiopian context.",
      link: "/docs/research/disability-inclusive-development-and-social-protection-ethiopia.pdf",
      download:
        "/docs/research/disability-inclusive-development-and-social-protection-ethiopia.pdf",
    },
  ];

  // Government & partner reports
  const reports = [
    {
      title: "AAAPD & OPDs – Annual Disability Inclusion & Advocacy Report",
      year: "Example template",
      level: "Organization-level reporting",
      tag: "Annual Report",
      description:
        "Template for documenting major achievements, advocacy actions, trainings, case stories and quantitative data from AAAPD and member OPDs each year.",
      link: "/docs/reports/aaapd-annual-disability-inclusion-report-template.pdf",
      download:
        "/docs/reports/aaapd-annual-disability-inclusion-report-template.pdf",
    },
    {
      title:
        "Joint Monitoring Report on Accessibility of Public Services in Addis Ababa",
      year: "Example template",
      level: "City-level monitoring",
      tag: "Monitoring Tool",
      description:
        "Structured tool to document accessibility of health centres, schools, transport hubs and public offices across sub-cities – ratings, photos, recommendations.",
      link: "/docs/reports/public-services-accessibility-monitoring-tool.pdf",
      download:
        "/docs/reports/public-services-accessibility-monitoring-tool.pdf",
    },
    {
      title: "Disability Data & Inclusion Dashboard – Indicator Framework",
      year: "Guidance note",
      level: "Data & M&E",
      tag: "Indicators",
      description:
        "Proposed list of key indicators for AAAPD and OPDs to track: membership, participation in meetings, access to services, livelihood support, accessibility actions and policy wins.",
      link: "/docs/reports/disability-inclusion-indicator-framework.pdf",
      download:
        "/docs/reports/disability-inclusion-indicator-framework.pdf",
    },
  ];


  // Toolkits & practical guidelines
  const toolkits = [
    {
      title: "Community-Based Rehabilitation (CBR) Guidelines",
      year: "WHO, 2010",
      level: "Health, education, livelihood, social & empowerment",
      tag: "CBR Toolkit",
      description:
        "Practical guidance for community-based rehabilitation programmes – how local OPDs, health workers and communities can support participation and inclusion.",
      link: "/docs/toolkits/who-cbr-guidelines.pdf",
      download: "/docs/toolkits/who-cbr-guidelines.pdf",
    },
    {
      title: "Disability-Inclusive Project Cycle Management Toolkit",
      year: "Operational guide",
      level: "NGOs & OPDs",
      tag: "Project Toolkit",
      description:
        "Step-by-step tools for mainstreaming disability in project design, proposal writing, budgeting, implementation and evaluation – checklists and practical examples.",
      link: "/docs/toolkits/disability-mainstreaming-toolkit-local-ngos.pdf",
      download:
        "/docs/toolkits/disability-mainstreaming-toolkit-local-ngos.pdf",
    },
    {
      title: "Inclusive Education Toolkit for Teachers and School Leaders",
      year: "Teacher support",
      level: "Education sector",
      tag: "Teacher Toolkit",
      description:
        "Classroom strategies, simple screening tools, individual education planning templates and low-cost adaptations to support learners with different types of disabilities.",
      link: "/docs/toolkits/inclusive-education-teacher-toolkit.pdf",
      download: "/docs/toolkits/inclusive-education-teacher-toolkit.pdf",
    },
    {
      title: "Disability-Sensitive Communication & Advocacy Guide",
      year: "Communication guide",
      level: "OPDs & partners",
      tag: "Advocacy Toolkit",
      description:
        "Guidance on respectful language, accessible information formats, inclusive events, media engagement and storytelling for disability rights campaigns.",
      link: "/docs/toolkits/disability-sensitive-communication-advocacy-guide.pdf",
      download:
        "/docs/toolkits/disability-sensitive-communication-advocacy-guide.pdf",
    },
  ];

  return (
    <div
      style={{ backgroundColor: "var(--bg-secondary)", color: "var(--text-primary)" }}
      className="min-h-screen flex flex-col"
    >
      <HomeNav />

      <main id="main" className="flex-1">
        {/* Hero / Intro */}
        <section
          className="relative bg-cover bg-center"
          style={{ backgroundImage: "url('/images/policies-hero.png')" }}
        >
          <div className="absolute inset-0 bg-slate-900/75" />
          <div className="relative max-w-7xl mx-auto px-6 py-24 md:py-32 flex flex-col md:flex-row items-center gap-10">
            <div className="flex-1">
              <p className="text-sm font-semibold tracking-wide text-teal-300 uppercase mb-3">
                {t('policiesHeroPill')}
              </p>
              <h1 className="text-3xl md:text-5xl font-extrabold text-white leading-tight">
                {t('policiesHeroTitlePrefix')}{" "}
                <span className="text-teal-300">{t('policiesHeroTitleHighlight')}</span>
              </h1>
              <p className="mt-5 text-base md:text-lg text-slate-200 max-w-2xl">
                {t('policiesHeroDesc')}
              </p>
            </div>


            <div className="flex-1 max-w-md w-full">
              <div className="bg-white/95 rounded-2xl shadow-2xl p-6 md:p-8 backdrop-blur">
                <div className="flex items-center mb-4">
                  <div className="h-10 w-10 rounded-xl bg-teal-600 text-white flex items-center justify-center mr-3">
                    <BookOpenIcon className="h-6 w-6" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase">{t('policiesQuickOverview')}</p>
                    <p className="text-sm text-slate-700">{t('policiesQuickOverviewDesc')}</p>
                  </div>
                </div>
                <ul className="space-y-2 text-sm text-slate-700">
                  <li>• {t('policiesBullet1')}</li>
                  <li>• {t('policiesBullet2')}</li>
                  <li>• {t('policiesBullet3')}</li>
                  <li>• {t('policiesBullet4')}</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* National policies */}
        <section
          className="py-20"
          style={{ backgroundColor: "var(--bg-primary)" }}
        >
          <div className="max-w-7xl mx-auto px-6">
            <SectionHeader
              title={t('policiesNationalTitle')}
              subtitle={t('policiesNationalSubtitle')}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {nationalPolicies.map((item) => {
                const isCRPD = item.title === "UN Convention on the Rights of Persons with Disabilities (CRPD) – Amharic & English";
                if (isCRPD) {
                  return (
                    <div key={item.title} className="md:col-span-2">
                      <div className="max-w-2xl mx-auto">
                        <ResourceCard resource={item} />
                      </div>
                    </div>
                  );
                }
                return <ResourceCard key={item.title} resource={item} />;
              })}
            </div>
          </div>
        </section>

        {/* International & regional frameworks */}
        <section
          className="py-20"
          style={{ backgroundColor: "var(--bg-secondary)" }}
        >
          <div className="max-w-7xl mx-auto px-6">
            <SectionHeader
              title={t('policiesInternationalTitle')}
              subtitle={t('policiesInternationalSubtitle')}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {internationalFrameworks.map((item) => (
                <ResourceCard key={item.title} resource={item} />
              ))}
            </div>
          </div>
        </section>

        {/* Research & academic work */}
        <section
          className="py-20"
          style={{ backgroundColor: "var(--bg-primary)" }}
        >
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 mb-10">
              <SectionHeader
                title={t('policiesResearchTitle')}
                subtitle={t('policiesResearchSubtitle')}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {researchPapers.map((item) => (
                <ResourceCard key={item.title} resource={item} />
              ))}
            </div>
          </div>
        </section>

        {/* Reports & monitoring tools */}
        <section
          className="py-20"
          style={{ backgroundColor: "var(--bg-secondary)" }}
        >
          <div className="max-w-7xl mx-auto px-6">
            <SectionHeader
              title={t('policiesReportsTitle')}
              subtitle={t('policiesReportsSubtitle')}
            />


            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {reports.map((item) => (
                <ResourceCard key={item.title} resource={item} />
              ))}
            </div>
          </div>
        </section>

        {/* Toolkits & practical guidelines */}
        <section
          className="py-20"
          style={{ backgroundColor: "var(--bg-primary)" }}
        >
          <div className="max-w-7xl mx-auto px-6">
            <SectionHeader
              title={t('policiesToolkitsTitle')}
              subtitle={t('policiesToolkitsSubtitle')}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {toolkits.map((item) => (
                <ResourceCard key={item.title} resource={item} />
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default DisabilityPoliciesPage;
